import { useEffect, useRef, useState } from "react";
import { TOUCH_TARGET } from "@/components/buttonStyles";
import { ArrowLeft } from "lucide-react";
import { CarrierQuestions } from "@/components/submissions/CarrierQuestions";
import { CarrierDeclined } from "@/components/submissions/CarrierDeclined";
import { CarrierStepProblem } from "@/components/submissions/CarrierStepProblem";
import { QuoteProgress } from "@/components/submissions/QuoteProgress";
import { formatUsd } from "@/lib/displayFormat";
import type {
  IndicationResult,
  CarrierDecline,
  MissingField,
} from "@/lib/submissions/brokerSubmissions";
import {
  fetchingNow,
  firmResultLine,
  type QuoteCard,
} from "@/lib/submissions/quoteCards";

// BRK-S347 — a carrier's quote, in its popup: its questions (BRK-S290), then
// its price, then what came back, worded as the carrier's step always has.
// The popup can close at any point and the run goes on: the page's loading
// screen shows it, then its card (BRK-S460).
// The questions mount only for a run the broker started and is answering,
// never from a URL, since a carrier with nothing to ask is quoted the moment
// they load. A Workers' Comp submission's carriers are asked on the wizard's
// last step instead (BRK-S393): `asks` false, the popup prices only, and a
// carrier with questions left sends the broker there.

export function QuoteStep({
  submissionId,
  card,
  onBack,
  onComplete,
  onRetry,
  onWatch,
  onCompleteProfile,
  missingFieldHref,
  onDeclineChange,
  asks = true,
}: {
  submissionId: string;
  card: QuoteCard;
  /** Back to the offer's details. */
  onBack: () => void;
  /** The carrier's set is complete: request its quote. A refusal is thrown
   *  back to the questions, which show it in this carrier's place. */
  onComplete: () => Promise<void>;
  onRetry: () => void;
  /** Whether this carrier's questions are on screen to show a refusal. */
  onWatch: (watching: boolean) => void;
  onCompleteProfile?: () => void;
  missingFieldHref?: (field: MissingField) => string | null;
  onDeclineChange: (declined: CarrierDecline | undefined) => void;
  /** Whether the popup asks the carrier's questions; false when they're
   *  asked elsewhere. */
  asks?: boolean;
}) {
  const run = card.run;
  const carrierId = card.column.carrierId;
  // The questions stay up while the carrier prices, for a refusal to land in
  // them; a popup opened mid-price shows the price's progress instead.
  const [questionsOpen, setQuestionsOpen] = useState(run?.kind === "answering");
  useEffect(() => {
    if (run?.kind === "answering") setQuestionsOpen(true);
    else if (run?.kind === "done" || run?.kind === "failed")
      setQuestionsOpen(false);
  }, [run?.kind]);
  const showQuestions =
    asks &&
    questionsOpen &&
    carrierId !== null &&
    (run?.kind === "answering" || run?.kind === "pricing");

  const watchRef = useRef(onWatch);
  watchRef.current = onWatch;
  useEffect(() => {
    if (!showQuestions) return;
    watchRef.current(true);
    return () => watchRef.current(false);
  }, [showQuestions]);

  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={onBack}
          className={`inline-flex items-center gap-1 self-start text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
        >
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          Details
        </button>
        <h3
          ref={heading}
          tabIndex={-1}
          className="text-subheading text-brk-text focus:outline-none"
        >
          Quote
        </h3>
        <p className="text-sm text-brk-dim">
          {asks
            ? `${card.carrier}’s quote is requested as soon as its questions are done. Your answers save as you go.`
            : `${card.carrier} prices the application as you submitted it.`}
        </p>
      </div>
      {run?.kind === "answering" && run.note && (
        <p role="status" className="text-sm text-brk-dim">
          {run.note}
        </p>
      )}
      {/* With its questions on screen, they show the stages themselves. A
          request PMS already holds is on its way all the same (BRK-S460). */}
      {fetchingNow(run) && !showQuestions && (
        <QuoteProgress carrier={card.carrier} at={2} withQuestions={asks} />
      )}
      {run?.kind === "waiting" && !run.inProgress && (
        <p role="status" className="text-sm text-brk-dim">
          {card.summary}
        </p>
      )}
      {run?.kind === "failed" && (
        <div className="flex flex-col gap-2">
          <CarrierStepProblem
            problem={run.problem}
            carrier={card.carrier}
            onCompleteProfile={onCompleteProfile}
            missingFieldHref={missingFieldHref}
            onRetry={onRetry}
          />
          {run.problem.kind !== "unavailable" &&
            run.problem.kind !== "other" && (
              <button
                type="button"
                onClick={onRetry}
                className={`self-start text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
              >
                Try again
              </button>
            )}
        </div>
      )}
      {run?.kind === "done" && (
        <>
          <RunResult carrier={card.carrier} result={run.result} />
          {run.result.outcome === "declined" && !run.result.quote && (
            <button
              type="button"
              onClick={onRetry}
              className={`self-start text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Change answers
            </button>
          )}
        </>
      )}
      {showQuestions && carrierId && (
        <CarrierQuestions
          submissionId={submissionId}
          carrierId={carrierId}
          carrier={card.carrier}
          onComplete={onComplete}
          onCompleteProfile={onCompleteProfile}
          missingFieldHref={missingFieldHref}
          onDeclineChange={onDeclineChange}
          editDeclined={card.chip.label === "Declined"}
        />
      )}
    </div>
  );
}

/** One carrier's quote result, as PMS returned it. */
function RunResult({
  carrier,
  result,
}: {
  carrier: string;
  result: IndicationResult;
}) {
  if (result.outcome === "declined" && !result.quote)
    return (
      <CarrierDeclined carrier={carrier} reasons={result.declineReasons} />
    );
  const reasons =
    result.outcome === "declined"
      ? result.declineReasons
      : result.outcome === "referred"
        ? result.referReasons
        : [];
  return (
    <div role="status" className="flex flex-col gap-1 text-sm">
      <span className="text-brk-text">{firmResultLine(carrier, result)}</span>
      {result.quote && result.outcome !== "declined" && (
        <span className="tabular-nums text-brk-text">
          Total premium {formatUsd(result.quote.totalPremium)}
        </span>
      )}
      {result.outcome === "referred" && reasons.length > 0 && (
        <h4 className="mt-1 text-subheading text-brk-text">
          Why it was referred
        </h4>
      )}
      {reasons.length > 0 && (
        <ul className="list-inside list-disc text-brk-dim">
          {reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
